import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { gql } from "@apollo/client";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { Device } from "../../types/device";
import { usePermissions } from "../auth/api";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import {
  fetchReaderStates,
  moveToUat,
  moveReader,
  returnToConnected,
  MoveResult,
  MoveStep,
  ReaderState,
} from "./appUpdatesApi";
import { MoveDestinationModal } from "./MoveDestinationModal";
import { useOrganizationStore } from "../../store/organization";
import { useEventStore } from "../../store/event";

// --------------------------------------------------------------------------
// Data
// --------------------------------------------------------------------------

const client = createApolloClient();

// Pull the readers we can manage from Hasura. A device row represents a
// tablet + paired Stripe reader, so the card reader's SN lives on
// card_reader_serial (the tablet's own manufacturer/model_number stay on
// the row). Filter only on having a non-empty SN — ops can narrow further
// via the search box.
const GET_READER_DEVICES = gql`
  query readerDevices($extraWhere: devices_bool_exp!) {
    devices(
      where: {
        _and: [
          { card_reader_serial: { _is_null: false, _neq: "" } }
          # S700 = STR70Z…, S710 = STR71Z…, both prefixed STR.
          # Excluding CHB-prefixed M2/BBPOS and WPC-prefixed WisePOS E here
          # because this page only manages the S700/S710 smart-reader fleet.
          { card_reader_serial: { _ilike: "STR%" } }
          $extraWhere
        ]
      }
      order_by: { card_reader_serial: asc }
    ) {
      id
      card_reader_serial
      mac_address
      manufacturer
      model_number
      license_plate
      current_possession
      deployment_status
      deployed_event_name
      updated_at
    }
  }
`;

// --------------------------------------------------------------------------
// Types — local UI state
// --------------------------------------------------------------------------

type FlightKind = "moving_to_uat" | "moving_to_connected" | null;

interface FlightStep {
  step: MoveStep["step"];
  label: string;
  status: "pending" | "in_progress" | "ok" | "error";
  message?: string;
}

interface FlightState {
  kind: Exclude<FlightKind, null>;
  // Step statuses; backend response replaces these once it returns.
  steps: FlightStep[];
}

interface Row {
  device: Device;
  reader?: ReaderState; // undefined = still loading; reader.found can be false
  flight?: FlightState;
  // Locally-marked verified (ops clicked "Mark update verified" before
  // backend metadata flip exists). Persists for the session only.
  locallyVerified?: boolean;
}

const STEP_LABELS: Record<MoveStep["step"], string> = {
  update_reader_location: "Moving reader to its new location",
  deregister_from_connected: "Deregistering from current account",
  register_on_platform_uat: "Registering on destination",
  deregister_from_platform: "Deregistering from current location",
  register_on_connected: "Registering on destination account",
  locate_reader: "Locating reader",
};

const labelFor = (step: MoveStep["step"]): string => STEP_LABELS[step] || step;

const moveToUatSteps = (): FlightStep[] => [
  { step: "update_reader_location", label: "Moving reader to RoninPOS UAT", status: "in_progress" },
];

const returnSteps = (): FlightStep[] => [
  { step: "update_reader_location", label: "Returning reader to its original location", status: "in_progress" },
];

// --------------------------------------------------------------------------
// Visual helpers
// --------------------------------------------------------------------------

const StatusPill = ({ row }: { row: Row }) => {
  const reader = row.reader;
  if (!reader) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Loading…</span>;
  }
  if (!reader.found) {
    if (reader.error === "state_fetch_failed") {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="Backend Stripe-state endpoint not reachable">
          State unavailable
        </span>
      );
    }
    // "search_incomplete" means at least one Stripe account could not be
    // searched (rate limit / transient error) — NOT that the reader is
    // unregistered. Showing "Not registered" there has sent ops to re-register
    // readers that were already registered.
    if (reader.error === "search_incomplete") {
      return (
        <span
          className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          title="One or more Stripe accounts could not be searched, so this is not conclusive. Refresh to retry."
        >
          Search incomplete
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title={reader.error ?? "Not found on the platform account"}>
        Not registered
      </span>
    );
  }
  if (reader.is_at_uat) {
    const verified =
      row.locallyVerified || reader.metadata?.ronin_app_update_state === "verified";
    return verified ? (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">At UAT · update verified</span>
    ) : (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">At UAT · waiting for update</span>
    );
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Deployed</span>;
};

// Classify a row into the same buckets the count pills show. Centralizing this
// lets the summary numbers AND the status filter agree by construction.
type StatusBucket =
  | "deployed"
  | "uat_pending"
  | "uat_verified"
  | "unavailable"
  | "unknown";

const bucketFor = (row: Row): StatusBucket => {
  if (!row.reader) return "unknown";
  if (!row.reader.found) {
    return row.reader.error === "state_fetch_failed" ||
      row.reader.error === "search_incomplete"
      ? "unavailable"
      : "unknown";
  }
  if (!row.reader.is_at_uat) return "deployed";
  const verified =
    row.locallyVerified || row.reader.metadata?.ronin_app_update_state === "verified";
  return verified ? "uat_verified" : "uat_pending";
};

const ProgressStrip = ({ flight }: { flight: FlightState }) => {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {flight.steps.map((s, i) => (
        <div key={s.step} className="flex items-center gap-2 text-xs">
          <span
            className={
              s.status === "ok"
                ? "inline-block w-4 h-4 rounded-full bg-green-500 text-white text-[10px] leading-4 text-center"
                : s.status === "error"
                  ? "inline-block w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center"
                  : s.status === "in_progress"
                    ? "inline-block w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"
                    : "inline-block w-4 h-4 rounded-full border border-gray-300"
            }
          >
            {s.status === "ok" ? "✓" : s.status === "error" ? "!" : ""}
          </span>
          <span
            className={
              s.status === "ok"
                ? "text-green-700"
                : s.status === "error"
                  ? "text-red-700"
                  : s.status === "in_progress"
                    ? "text-blue-700 font-medium"
                    : "text-gray-500"
            }
          >
            Step {i + 1}: {s.label}
            {s.message && s.status === "error" ? ` — ${s.message}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
};

// --------------------------------------------------------------------------
// Page
// --------------------------------------------------------------------------

export const AppUpdates = () => {
  const { editCreatePermission } = usePermissions("devices");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pickerOpenForId, setPickerOpenForId] = useState<number | null>(null);
  // null = no filter. Clicking a count pill toggles it on/off.
  const [statusFilter, setStatusFilter] = useState<StatusBucket | null>(null);

  // Honor the global Organization / Event dropdowns in the header. We mirror
  // the same approach DeviceList.tsx uses: filter on the QB-sourced
  // `current_possession` / `deployed_event_name` strings rather than
  // `assigned_event_id`, because almost no devices have the latter populated
  // (QB only fills it for a small subset of inventory rows).
  const organizationId = useOrganizationStore((s) => s.organizationId);
  const organizations = useOrganizationStore((s) => s.organizations);
  const eventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);

  const headerOrgName = useMemo(() => {
    if (!organizationId) return null;
    const o = (organizations ?? []).find((x: any) => Number(x?.id) === Number(organizationId));
    return o?.name ?? null;
  }, [organizations, organizationId]);

  const headerEventName = useMemo(() => {
    if (!eventId) return null;
    const e = (allEvents ?? []).find((x: any) => Number(x?.id) === Number(eventId));
    return e?.name ?? null;
  }, [allEvents, eventId]);

  // Rules (mirroring DeviceList):
  //   - Specific event chosen → deployed_event_name ILIKE %eventName%
  //   - Org chosen, "All Events" → current_possession ILIKE %orgName%
  //   - Neither → no extra filter
  // _ilike + wildcards because QB stores names like "M Culinary - USGA
  // Women's Open 2026" — the event name is a substring of the deployed_event_name.
  const extraWhere = useMemo<Record<string, any>>(() => {
    if (eventId && headerEventName) {
      return { deployed_event_name: { _ilike: `%${headerEventName}%` } };
    }
    if (organizationId && headerOrgName) {
      return { current_possession: { _ilike: `%${headerOrgName}%` } };
    }
    return {};
  }, [eventId, headerEventName, organizationId, headerOrgName]);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await client.query({
        query: GET_READER_DEVICES,
        variables: { extraWhere },
        fetchPolicy: "network-only",
      });
      const devices = (data?.devices ?? []) as Device[];
      // Initialize rows with no reader state yet.
      const initial: Row[] = devices.map((d) => ({ device: d }));
      setRows(initial);

      // Batch-fetch Stripe state.
      const sns = devices
        .map((d) => d.card_reader_serial)
        .filter((s): s is string => !!s);
      if (sns.length > 0) {
        try {
          const readers = await fetchReaderStates(sns);
          const bySn = new Map(readers.map((r) => [r.serial_number, r]));
          setRows((prev) =>
            prev.map((r) => ({
              ...r,
              reader: r.device.card_reader_serial
                ? bySn.get(r.device.card_reader_serial) ?? { serial_number: r.device.card_reader_serial, found: false, error: "no_response" }
                : undefined,
            }))
          );
        } catch (e: any) {
          // Don't fail the whole page if the backend endpoint isn't up yet —
          // surface a banner AND mark each row as state-unavailable so they
          // don't get stuck on "Loading…".
          setError(`Could not load Stripe state: ${e?.message ?? "unknown"}`);
          setRows((prev) =>
            prev.map((r) => ({
              ...r,
              reader: r.device.card_reader_serial
                ? { serial_number: r.device.card_reader_serial, found: false, error: "state_fetch_failed" }
                : undefined,
            }))
          );
        }
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, [extraWhere]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // ----------------------------------------------------------------------
  // Per-row actions
  // ----------------------------------------------------------------------

  const applyMoveResult = (deviceId: number, result: MoveResult) => {
    // Backend returns whichever steps it actually ran (1 step for same-account
    // moves, 2 steps for cross-account moves). Replace the placeholder
    // FlightStep[] with the real shape.
    const finalisedSteps: FlightStep[] = result.steps.map((s) => ({
      step: s.step,
      label: labelFor(s.step),
      status:
        s.status === "ok" ? "ok" :
        s.status === "error" ? "error" :
        "pending",
      message: s.message,
    }));
    setRows((prev) =>
      prev.map((r) => {
        if (r.device.id !== deviceId) return r;
        // Show the finalised steps briefly, then clear flight state and
        // adopt the new reader state.
        return {
          ...r,
          flight: { kind: r.flight!.kind, steps: finalisedSteps },
          reader: result.reader,
          locallyVerified: false,
        };
      })
    );
    // Clear the progress strip after a moment.
    setTimeout(() => {
      setRows((prev) =>
        prev.map((r) => (r.device.id === deviceId ? { ...r, flight: undefined } : r))
      );
    }, 1500);
  };

  const handleMoveToUat = async (row: Row) => {
    const sn = row.device.card_reader_serial;
    const reader = row.reader;
    if (!sn || !reader?.found || reader.is_at_uat) {
      toast.error("Reader isn't eligible to move to UAT.");
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        r.device.id === row.device.id
          ? { ...r, flight: { kind: "moving_to_uat", steps: moveToUatSteps() } }
          : r
      )
    );
    try {
      const result = await moveToUat({ serial_number: sn });
      applyMoveResult(row.device.id, result);
      toast.success(`${sn} moved to UAT`);
    } catch (e: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.device.id === row.device.id
            ? {
                ...r,
                flight: {
                  kind: "moving_to_uat",
                  steps: (r.flight?.steps ?? moveToUatSteps()).map((s) =>
                    s.status === "in_progress" ? { ...s, status: "error", message: e?.message } : s
                  ),
                },
              }
            : r
        )
      );
      toast.error(`Move failed: ${e?.message ?? "unknown"}`);
    }
  };

  const handleReturn = async (row: Row) => {
    const sn = row.device.card_reader_serial;
    const reader = row.reader;
    if (!sn || !reader?.found || !reader.is_at_uat) {
      toast.error("Reader isn't at UAT.");
      return;
    }
    const originLocation = reader.metadata?.ronin_origin_location_id;
    if (!originLocation) {
      toast.error(
        "No origin location on this reader — ops needs to pick a destination. (Picker UI coming next.)"
      );
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        r.device.id === row.device.id
          ? { ...r, flight: { kind: "moving_to_connected", steps: returnSteps() } }
          : r
      )
    );
    try {
      const result = await returnToConnected({ serial_number: sn });
      applyMoveResult(row.device.id, result);
      toast.success(`${sn} returned to its original location`);
    } catch (e: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.device.id === row.device.id
            ? {
                ...r,
                flight: {
                  kind: "moving_to_connected",
                  steps: (r.flight?.steps ?? returnSteps()).map((s) =>
                    s.status === "in_progress" ? { ...s, status: "error", message: e?.message } : s
                  ),
                },
              }
            : r
        )
      );
      toast.error(`Return failed: ${e?.message ?? "unknown"}`);
    }
  };

  const handleMoveToDestination = async (
    row: Row,
    destination: {
      destination_account_id: string;
      destination_location_id: string;
      account_name: string;
      location_name: string;
    }
  ) => {
    const sn = row.device.card_reader_serial;
    if (!sn) throw new Error("This device has no card reader serial.");
    setRows((prev) =>
      prev.map((r) =>
        r.device.id === row.device.id
          ? {
              ...r,
              flight: {
                kind: "moving_to_connected",
                steps: [
                  {
                    step: "update_reader_location",
                    label: `Moving reader to ${destination.location_name}`,
                    status: "in_progress",
                  },
                ],
              },
            }
          : r
      )
    );
    try {
      const result = await moveReader({
        serial_number: sn,
        destination_account_id: destination.destination_account_id || undefined,
        destination_location_id: destination.destination_location_id,
      });
      applyMoveResult(row.device.id, result);
      toast.success(
        `${sn} moved to ${destination.location_name} (${destination.account_name})`
      );
    } catch (e: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.device.id === row.device.id ? { ...r, flight: undefined } : r
        )
      );
      toast.error(`Move failed: ${e?.message ?? "unknown"}`);
      throw e;
    }
  };

  const handleMarkVerified = (row: Row) => {
    setRows((prev) =>
      prev.map((r) => (r.device.id === row.device.id ? { ...r, locallyVerified: true } : r))
    );
  };

  // ----------------------------------------------------------------------
  // Derived view
  // ----------------------------------------------------------------------

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && bucketFor(r) !== statusFilter) return false;
      if (!q) return true;
      const d = r.device;
      return (
        (d.card_reader_serial ?? "").toLowerCase().includes(q) ||
        (d.mac_address ?? "").toLowerCase().includes(q) ||
        (d.license_plate ?? "").toLowerCase().includes(q) ||
        (d.current_possession ?? "").toLowerCase().includes(q) ||
        (r.reader?.location_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const summary = useMemo(() => {
    let atConnected = 0,
      atUatPending = 0,
      atUatVerified = 0,
      unavailable = 0,
      unknown = 0;
    for (const r of rows) {
      switch (bucketFor(r)) {
        case "deployed":      atConnected++;   break;
        case "uat_pending":   atUatPending++;  break;
        case "uat_verified":  atUatVerified++; break;
        case "unavailable":   unavailable++;   break;
        case "unknown":       unknown++;       break;
      }
    }
    return { atConnected, atUatPending, atUatVerified, unavailable, unknown, total: rows.length };
  }, [rows]);

  // Clicking a count pill toggles its filter on; clicking it again clears.
  const toggleStatus = (next: StatusBucket) =>
    setStatusFilter((cur) => (cur === next ? null : next));

  // CSV export of the currently-filtered rows. Built for the QB-typo workflow:
  // ops clicks "55 unknown", clicks Export, gets a sheet of SNs to fix in QB.
  // Honors the active status filter AND search box — what you see is what
  // gets exported.
  const handleExportCsv = () => {
    const escape = (v: string | number | null | undefined): string => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // RFC 4180: wrap in quotes when the field contains quotes, commas, or
      // line breaks; double-up any quotes within.
      if (/["\n\r,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const STATUS_LABELS: Record<StatusBucket, string> = {
      deployed: "Deployed",
      uat_pending: "At UAT (pending)",
      uat_verified: "At UAT (verified)",
      unavailable: "State unavailable",
      unknown: "Not registered",
    };

    const headers = [
      "License Plate",
      "Card Reader SN",
      "MAC Address",
      "Status",
      "Stripe Location",
      "Current Possession",
      "Last Deployed Event",
      "Reader Last Seen",
      "Internal Part Number",
      "Manufacturer",
      "Model Number",
    ];

    const lines = filtered.map((r) => {
      const d = r.device;
      const reader = r.reader;
      return [
        d.license_plate,
        d.card_reader_serial,
        d.mac_address,
        STATUS_LABELS[bucketFor(r)],
        reader?.found ? (reader.location_name ?? reader.location_id ?? "") : "",
        d.current_possession,
        d.deployed_event_name,
        reader?.last_seen_at ?? "",
        d.internal_part_number,
        d.manufacturer,
        d.model_number,
      ].map(escape).join(",");
    });

    // BOM so Excel opens UTF-8 cleanly (license plates may contain unicode).
    const csv = "﻿" + headers.map(escape).join(",") + "\n" + lines.join("\n") + "\n";

    const today = new Date().toISOString().slice(0, 10);
    const scope = statusFilter ?? "all";
    const filename = `app-updates-${scope}-${filtered.length}-${today}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <Link
              to="/devices/list"
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline inline-block mb-1"
            >
              ← Devices
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Smart Reader Management</h1>
            <div className="text-xs text-gray-500">
              Move smart readers (S700, S710, T600) to the Prod Update Location to receive the latest app, then return them to their connected account.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SecondaryButton
              onClick={handleExportCsv}
              isDisabled={filtered.length === 0}
            >
              Export CSV
            </SecondaryButton>
            <PrimaryButton onClick={loadDevices}>Refresh</PrimaryButton>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Search SN, MAC, license plate, location, possession"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {statusFilter ? (
              <button
                type="button"
                onClick={() => setStatusFilter(null)}
                className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                title="Clear the active status filter"
              >
                {filtered.length} of {summary.total} · clear filter ×
              </button>
            ) : (
              <span className="text-xs text-gray-500">{summary.total} readers</span>
            )}
            {summary.atConnected > 0 && (
              <button
                type="button"
                onClick={() => toggleStatus("deployed")}
                title="Click to show only readers deployed on a connected account"
                className={
                  "text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 " +
                  (statusFilter === "deployed"
                    ? "ring-2 ring-blue-400"
                    : "hover:bg-blue-100")
                }
              >
                {summary.atConnected} deployed
              </button>
            )}
            {summary.atUatPending > 0 && (
              <button
                type="button"
                onClick={() => toggleStatus("uat_pending")}
                title="Click to show only readers waiting for the UAT update"
                className={
                  "text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 " +
                  (statusFilter === "uat_pending"
                    ? "ring-2 ring-amber-400"
                    : "hover:bg-amber-100")
                }
              >
                {summary.atUatPending} at UAT (pending)
              </button>
            )}
            {summary.atUatVerified > 0 && (
              <button
                type="button"
                onClick={() => toggleStatus("uat_verified")}
                title="Click to show only readers whose UAT update is verified"
                className={
                  "text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 " +
                  (statusFilter === "uat_verified"
                    ? "ring-2 ring-green-400"
                    : "hover:bg-green-100")
                }
              >
                {summary.atUatVerified} at UAT (verified)
              </button>
            )}
            {summary.unavailable > 0 && (
              <button
                type="button"
                onClick={() => toggleStatus("unavailable")}
                title="Backend state endpoint was unreachable for these readers"
                className={
                  "text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 " +
                  (statusFilter === "unavailable"
                    ? "ring-2 ring-gray-400"
                    : "hover:bg-gray-200")
                }
              >
                {summary.unavailable} state unavailable
              </button>
            )}
            {summary.unknown > 0 && (
              <button
                type="button"
                onClick={() => toggleStatus("unknown")}
                title="Click to show only readers Stripe doesn't recognize — usually a QB serial-number typo"
                className={
                  "text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 " +
                  (statusFilter === "unknown"
                    ? "ring-2 ring-gray-400"
                    : "hover:bg-gray-200")
                }
              >
                {summary.unknown} unknown
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading readers…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No readers match the current search.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const d = row.device;
            const reader = row.reader;
            const inFlight = !!row.flight;
            const verified =
              row.locallyVerified || reader?.metadata?.ronin_app_update_state === "verified";

            const canMoveToUat =
              !!editCreatePermission &&
              !inFlight &&
              !!reader?.found &&
              !reader.is_at_uat;

            const canReturn =
              !!editCreatePermission &&
              !inFlight &&
              !!reader?.found &&
              !!reader.is_at_uat &&
              !!verified;

            const showMarkVerified =
              !!editCreatePermission &&
              !inFlight &&
              !!reader?.found &&
              !!reader.is_at_uat &&
              !verified;

            const identityLine = [d.manufacturer, d.model_number, d.internal_part_number]
              .filter(Boolean)
              .join(" · ");

            // Stripe location wins when the reader is registered there. Otherwise
            // fall back to the QB current_possession, with the last-deployed event
            // tacked on when the possession is just a warehouse state like
            // "Active" — that's the case where the bare possession value isn't
            // self-explanatory (it doesn't tell ops which customer the device
            // was last with).
            const isWarehouseState = ((d.current_possession ?? "").trim().toLowerCase() === "active");
            const possessionLine =
              isWarehouseState && d.deployed_event_name
                ? `${d.current_possession} · last deployed: ${d.deployed_event_name}`
                : (d.current_possession ?? "");
            const locationLine = reader?.found
              ? (reader.location_name ?? reader.location_id ?? "")
              : possessionLine;

            return (
              <div
                key={d.id}
                className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-base">
                        {d.card_reader_serial ?? `Device #${d.id}`}
                      </span>
                      <StatusPill row={row} />
                      {reader?.found && reader.status && (
                        <span
                          className={
                            reader.status === "online"
                              ? "text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                              : "text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"
                          }
                          title={
                            reader.status === "online"
                              ? "Reader is currently online and heartbeating to Stripe"
                              : "Reader is offline — it can't pull an update until it boots"
                          }
                        >
                          {reader.status === "online" ? "● Online" : "○ Offline"}
                        </span>
                      )}
                      {d.license_plate && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          #{d.license_plate}
                        </span>
                      )}
                    </div>
                    {identityLine && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{identityLine}</div>
                    )}
                    {locationLine && (
                      <div className="text-xs text-gray-700 mt-0.5 truncate">
                        Stripe location: {locationLine}
                      </div>
                    )}
                    {reader?.found && reader.is_at_uat && !verified && (
                      <div className="text-xs text-amber-800 mt-0.5 font-medium">
                        Reboot the device to apply the update
                      </div>
                    )}
                    {reader?.last_seen_at && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Reader last seen {moment(reader.last_seen_at).fromNow()}
                      </div>
                    )}
                    {row.flight && <ProgressStrip flight={row.flight} />}
                  </div>

                  <div className="flex flex-row items-center justify-end gap-2 flex-shrink-0 flex-wrap">
                    {canMoveToUat && (
                      <PrimaryButton onClick={() => handleMoveToUat(row)}>
                        Move to Prod Update Location
                      </PrimaryButton>
                    )}
                    {!!editCreatePermission && !inFlight && !!reader?.found && (
                      <SecondaryButton onClick={() => setPickerOpenForId(row.device.id)}>
                        Move to…
                      </SecondaryButton>
                    )}
                    {showMarkVerified && (
                      <SecondaryButton onClick={() => handleMarkVerified(row)}>
                        Mark update verified
                      </SecondaryButton>
                    )}
                    {canReturn && (
                      <PrimaryButton onClick={() => handleReturn(row)}>
                        Return to connected account
                      </PrimaryButton>
                    )}
                    {!editCreatePermission && reader?.found && (
                      <span className="text-xs text-gray-400" title="Your role lacks devices edit/create permission">Read-only</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MoveDestinationModal
        open={pickerOpenForId !== null}
        serialNumber={
          pickerOpenForId !== null
            ? rows.find((r) => r.device.id === pickerOpenForId)?.device.card_reader_serial ?? null
            : null
        }
        currentAccountId={
          pickerOpenForId !== null
            ? rows.find((r) => r.device.id === pickerOpenForId)?.reader?.connected_account_id ?? ""
            : ""
        }
        currentLocationId={
          pickerOpenForId !== null
            ? rows.find((r) => r.device.id === pickerOpenForId)?.reader?.location_id ?? null
            : null
        }
        onClose={() => setPickerOpenForId(null)}
        onConfirm={async (dest) => {
          const target = rows.find((r) => r.device.id === pickerOpenForId);
          if (!target) return;
          await handleMoveToDestination(target, dest);
        }}
      />
    </div>
  );
};

// Loader is a no-op — all fetches happen in the component so ops can refresh
// without going through the router.
export const appUpdatesLoader = async () => null;
